// src/screens/s3/Longevity.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Longevity Scenario
// Make sure your money lasts as long as you do.
// Always shown. No skip. No commit. hideCommitButtons=true
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import ScenarioScreenWrapper from './ScenarioScreenWrapper';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function Longevity({ data, onUpdate }: Props) {
  const voyantNote = 'Extend the plan to age 95. At what age does the pot run out?';

  const fundedToAge = data.s3LongevityFundedToAge || 0;
  const isFunded = fundedToAge >= 95;
  const gapYears = 95 - fundedToAge;

  const resultCard = isFunded
    ? <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <p className="text-sm font-bold text-green-800">✓ Funded to 95</p>
        <p className="text-xs text-green-700 mt-1">Your plan provides for a long and financially secure life.</p>
      </div>
    : fundedToAge > 0
      ? <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <p className="text-sm font-bold text-amber-800">⚠ Pot depletes at {fundedToAge}</p>
          <p className="text-xs text-amber-700 mt-1">
            There is a {gapYears}-year gap ({95 - fundedToAge} years short of 95).
          </p>
        </div>
      : null;

  return (
    <ScenarioScreenWrapper
      title="Longevity"
      question="Let's make sure your money lasts as long as you do. What if you live well into your nineties?"
      clientName={data.clientName}
      voyantNote={voyantNote}
      isCommitted={false}
      hideCommitButtons={true}
      voyantReadbacks={
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
              Pot Funded To Age
            </label>
            <input
              type="number"
              min="65"
              max="100"
              value={fundedToAge || ''}
              onChange={(e) => onUpdate({ s3LongevityFundedToAge: e.target.value === '' ? 0 : Number(e.target.value) })}
              className="text-3xl font-bold text-ma-navy bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 w-32 focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
            />
          </div>

          {fundedToAge > 0 && resultCard}
        </div>
      }
    >
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Adviser Instruction:</strong> Extend the projection to age 95 and identify the age at which the portfolio would be depleted.
        </p>
      </div>
    </ScenarioScreenWrapper>
  );
}
